<template>
  <div class="articles-container">
    <h2 class="h3">
      Difference in Means Calculator
    </h2>
    <div id="heading1" class="article-section">
      <h3 class="h4">
        Hypothesis Testing the Difference in Means
      </h3>
      <p>Hypothesis testing is a statistical method used to assess how valid a claim about a population or two is, based on sample data. It helps researchers make informed decisions and draw meaningful conclusions in various fields, from scientific research, business, and more.</p>
      <p>Ultimately, we form a null hypothesis that assumes no significant effect or relationship, and an alternative hypothesis that suggests the opposite. Through data analysis, we determine whether the evidence supports rejecting the null hypothesis in favor of the alternative, indicating that the observed results are unlikely to occur by chance.</p>
      <p>When conducting a hypothesis test between two means, follow <span class="bolded-texts">these six steps:</span></p>
      <ol>
        <li>State the hypothesis;</li>
        <li>Select an appropriate test for your hypothesis;</li>
        <li>Specify the significance level;</li>
        <li>State the decision rule;</li>
        <li>Calculate the test statistic for difference of means;</li>
        <li>Make a decision based on the result.</li>
      </ol>
      <img src="../../../../assets/images/calculators/difference-in-means/dif-in-means-001.webp" alt="Process of hypothesis testing graph.">
      <p>These steps constitute traditional hypothesis testing with two samples. Use our Difference in Means Calculator to swiftly find the difference in means of your two groups. Alternatively, if you’re dealing with a single population, try our Hypothesis Testing Calculator.</p>
    </div>

    <div id="heading2" class="article-section">
      <h3 class="h4">
        What are Hypothesis Tests for Differences of Means?
      </h3>
      <p>When comparing two groups, we often want to calculate a significant difference, if such exists in their means. The question is whether the observed difference is due to chance or a reflection of true differences in the underlying mean values. We find out by pulling two samples from each.</p>
      <p>Each sample we compare represents one of the two groups. We perform a <span class="bolded-texts">statistical test (e.g., t-test or z-test)</span> that allows us to assess if the observed difference in means is statistically significant. Thus, we gain insights into whether the two groups exhibit meaningful disparities in their mean values.</p>
    </div>

    <div id="heading3" class="article-section">
      <h3 class="h4">
        Two-Sided vs. One-Sided Hypotheses for Difference in Means
      </h3>
      <p>There are two types of hypotheses: two-sided and one-sided hypotheses.</p>
      <p class="no-margin">
        A two-sided hypothesis, also known as a two-tailed, determines if there is a significant difference between the means of two groups without specifying the direction. The null hypothesis assumes that the means are equal, while the alternative hypothesis states that they’re not:
      </p>
      <p class="formulas-overflow">
        \[H_0:\mu_1-\mu_2=0\]
      </p>
      <p class="formulas-overflow">
        \[H_1:\mu_1-\mu_2\neq0\]
      </p>
      <p class="no-margin">
        On the other hand, we use a one-sided (or one-tailed) hypothesis to calculate whether one mean is greater than or less than the other. In fact, the null hypothesis states that one mean is less than or equal to the other mean, while the alternative hypothesis states that one mean is greater than the other mean. For a right-sided test, we have the following:
      </p>
      <p class="formulas-overflow">
        \[H_0:\mu_1-\mu_2\le0\]
      </p>
      <p class="formulas-overflow">
        \[H_1:\mu_1-\mu_2>0\]
      </p>
      <p class="no-margin">
        And for a left-sided test, we have:
      </p>
      <p class="formulas-overflow">
        \[H_0:\mu_1-\mu_2\ge0\]
      </p>
      <p class="formulas-overflow">
        \[H_1:\mu_1-\mu_2&lt;0\]
      </p>
      <p>In summary, the main difference between one-sided and two-sided hypothesis tests lies in the directionality of the alternative hypothesis. Experiment with hypotheses with our Difference in Means Calculator.</p>
    </div>

    <div id="heading4" class="article-section">
      <h3 class="h4">
        T-Statistic for Difference in Means
      </h3>
      <p>The t-statistic plays a crucial role in hypothesis testing with two samples. It helps calculate the significant difference by telling us if it’s simply a result of random variation or not. In other words, it allows us to determine whether we will accept or reject the null hypothesis. The approach quantifies the size of the means difference relative to the variability within the samples. A larger t-statistic indicates a more pronounced difference.</p>
      <p class="no-margin">
        To compute the t-statistic, we subtract the hypothesized population difference (if any) from the observed sample difference, and then divide it by the standard error as follows:
      </p>
      <p class="formulas-overflow">
        \[T=\frac{(\bar{x}-\bar{y})-\mu_0}{\sqrt{\frac{\sigma_x^2}{n_x}+\frac{\sigma_y^2}{n_y}}}\]
      </p>
      <p>The standard error considers the variation within the samples and provides an estimate of the expected sampling variability.</p>
      <p>For quicker results, you can use our Difference in Mean Calculator to conduct a t-test.</p>
    </div>

    <div id="heading5" class="article-section">
      <h3 class="h4">
        Level of Significance and Rejection Region for Comparison of Means
      </h3>
      <p>The level of significance (α) is a critical parameter when testing the difference in means. It represents the probability of rejecting a true null hypothesis (otherwise known as making a Type I error). In hypothesis testing with two samples, researchers compare sample data from two distinct groups to assess whether there is a significant difference between them.</p>
      <p>By setting the level of significance (commonly at 0.05 or 0.01), we establish how much evidence is required to reject the null hypothesis. If the p-value (the probability of obtaining the observed results assuming the null hypothesis is true) is less than or equal to the chosen level of significance, we reject the null hypothesis and conclude that there is a statistically significant difference between the two populations. In contrast, if the p-value is greater than the chosen significance level, there is insufficient evidence to reject the null hypothesis and any observed difference in means are considered statistically insignificant.</p>
      <p>Choosing an appropriate level of significance is essential for drawing accurate conclusions and avoiding erroneous interpretations in hypothesis testing for two populations. Try out different α values and discover which one is best for your test with our Difference in Means Calculator.</p>
      <p>To determine whether to accept or reject the null hypothesis, we compare the calculated test statistic with the critical value from its relevant distribution at a chosen significance level. If the calculated test statistic for the difference in means falls within the critical region, we reject the null hypothesis, concluding that there is a significant difference between the means. If the calculated t-statistic falls outside the critical region, we fail to reject the null hypothesis, indicating insufficient evidence to support a significant difference.</p>
    </div>

    <div id="heading6" class="article-section">
      <h3 class="h4">
        P-Value for the Difference between Two Means
      </h3>
      <p>The p-value approach is an alternative method used in hypothesis testing to compare two means. It involves calculating the p-value associated with the observed t-statistic, which provides a measure of the strength of evidence against the null hypothesis.</p>
      <p>Instead of testing at preassigned levels of significance, we can find the smallest one at which we can still reject the null hypothesis, given the observed sample statistic. In other words, the p-value quantifies the likelihood of obtaining the observed difference in means by chance alone.</p>
      <p>We compare the p-value to a predetermined significance level (α). If it’s less than α, which is typically 0.05, it is considered statistically significant. In this case, we reject the null hypothesis and conclude that there is a significant difference between the means.</p>
      <p>Conversely, if the p-value is larger than α, we fail to reject the null hypothesis and conclude that there is insufficient evidence to support a significant difference.</p>
      <p>The p-value approach offers several advantages. It provides a quantitative measure of the evidence against the null hypothesis, allowing for more nuanced interpretations. Additionally, it allows researchers to set the desired level of significance based on their specific study requirements.</p>
      <p>Both the critical value and the p-value are valid methods for hypothesis testing with two samples. However, the latter has gained popularity due to its flexibility and ease of interpretation. It provides a more comprehensive understanding of the statistical significance of the observed results.</p>
      <p>Remember, the choice between the two approaches ultimately depends on the specific requirements of your analysis and the prevailing statistical conventions in your field.</p>
    </div>

    <div id="heading7" class="article-section">
      <h3 class="h4">
        How to Choose Test Statistic for the Difference between Two Means
      </h3>
      <p>When we compare means for two populations or samples in statistics, the choice of technique depends on the type of the data we have and the relationship between the groups. It is important to select the appropriate technique that aligns with the specific data characteristics and the objectives of the analysis. In general, samples can be grouped as <span class="bolded-texts">dependent</span> (also known as <span class="bolded-texts">paired</span>) and <span class="bolded-texts">independent</span>.</p>
      <h4 class="h5">
        Dependent Samples
      </h4>
      <p>With dependent samples, the data points within the first group share a common factor or are linked in with the second group’s observations in some way. This often occurs with “before-and after data”—information gathered about a subject or event at two separate points in time: before and after a change happens. For example, measuring the same person’s blood pressure before and after physical training, or the dividend policy of companies before and after a change in tax laws. When we want to conduct hypothesis tests for difference of means based on samples that we believe are dependent, we use <span class="bolded-texts">paired samples test</span> (also known as <span class="bolded-texts">paired comparisons test</span>). When the sample means are paired, the appropriate statistic to use depends on the sample size and whether the population variance of paired differences is known or unknown.</p>
      <p>We observe two types of dependent samples tests:</p>
      <ul class="ul">
        <li>Paired z-test</li>
        <li>Paired t-test</li>
      </ul>
      <p>Let’s look at each one in more detail.</p>
      <h4 class="h5">
        Paired Z-Test
      </h4>
      <p class="no-margin">
        If the population standard deviation of paired differences is known and the sample size is large (typically greater than 30), then we would use the z-statistic otherwise referred to as the paired z-test. We calculate it using the following formula:
      </p>
      <p class="formulas-overflow">
        \[Z=\frac{\bar{d}-\mu_0}{\sigma_d/\sqrt n}\]
      </p>
      <p>Here, we denote the elements as follows:</p>
      <ul class="ul">
        <li><span class="inline-formulas">\(\bar{d}\)</span> – the mean of sample differences</li>
        <li><span class="inline-formulas">\[\mu_0\]</span> – the hypothesized mean difference (usually 0)</li>
        <li><span class="inline-formulas">\(\sigma_d\)</span> – the population standard deviation of paired differences</li>
        <li><span class="inline-formulas">\[n\]</span> – the sample size</li>
      </ul>
      <p>Here are the assumptions behind the paired z-test</p>
      <ul class="ul">
        <li>The data is continuous (not discrete).</li>
        <li>The mean differences between the paired observations should follow a normal distribution.</li>
        <li>This is important as the z-test relies on the normality assumption for accurate inference.</li>
        <li>The differences between the paired observations should follow a normal distribution.</li>
        <li>The population standard deviation of paired differences is known.</li>
      </ul>
      <p class="no-margin">
        For the two random variables <span class="inline-formulas">\[X_a\]</span>, and <span class="inline-formulas">\[X_b\]</span>, <span class="inline-formulas">\[d_i\]</span> denotes the difference between the paired observations:
      </p>
      <p class="formulas-overflow">
        \[d_i=x_{Ai}-x_{Bi}\]
      </p>
      <p>In the formula, <span class="inline-formulas">\[x_{ai}\]</span> and <span class="inline-formulas">\[x_{bi}\]</span> are the ith pair of the observations. Meanwhile <span class="inline-formulas">\[u_d\]</span>, denotes the population mean difference and <span class="inline-formulas">\[u_{d0}\]</span> represents a hypothesized value for the population mean difference.</p>
      <p>Now, let’s examine the different types of hypothesis testing that we can have.</p>
      <p class="no-margin">
        For two-tailed test:
      </p>
      <p class="formulas-overflow">
        \[H_0:\mu_d=\mu_0\]
      </p>
      <p class="formulas-overflow">
        \[H_1:\mu_d\neq\mu_0\]
      </p>
      <p class="no-margin">
        For a right-sided test:
      </p>
      <p class="formulas-overflow">
        \[H_0:\mu_d\le\mu_0\]
      </p>
      <p class="formulas-overflow">
        \[H_1:\mu_d>\mu_0\]
      </p>
      <p class="no-margin">
        For a left-sided test:
      </p>
      <p class="formulas-overflow">
        \[H_0:\mu_d\ge\mu_0=0\]
      </p>
      <p class="formulas-overflow">
        \[H_1:\mu_d&lt;\mu_0=0\]
      </p>
      <p>Note that, in most cases <span class="inline-formulas">\[\mu_0\]</span>, is equal to 0.</p>
      <h4 class="h5">
        Paired T-Test
      </h4>
      <p class="no-margin">
        If the population variance of paired differences is <span class="bolded-texts">unknown</span> or the sample size is small (typically less than 30), the <span class="bolded-texts">paired t-test</span> is more appropriate to use. We calculate it as follows:
      </p>
      <p class="formulas-overflow">
        \[T=\frac{\bar{d}-\mu_0}{s_d/\sqrt n}\]
      </p>
      <p>We denote the elements in this formula in the following way:</p>
      <ul class="ul">
        <li><span class="inline-formulas">\(\bar{d}\)</span> – the mean of sample differences</li>
        <li><span class="inline-formulas">\[\mu_0\]</span> – the hypothesized mean difference (usually 0)</li>
        <li><span class="inline-formulas">\(s_d\)</span> – the sample standard deviation of paired differences</li>
        <li><span class="inline-formulas">\[n\]</span> – the sample size</li>
      </ul>
      <p>The paired sample t-test relies on several key assumptions:</p>
      <ul class="ul">
        <li>The samples are random and dependent. Each pair consists of two measurements that are related or connected in some way, such as before-and-after measurements or matched pairs from different groups.</li>
        <li>The standard deviations of both samples are unknown.</li>
        <li>The distribution of the differences between paired means is approximately normally distributed. While the t-test is known to be robust to deviations from normality, adhering to this assumption improves the reliability of the test results.</li>
      </ul>
      <h4 class="h5">
        Independent Samples
      </h4>
      <p>Independent samples refer to two unrelated or unpaired populations. To illustrate, let’s consider a scenario in which we investigate the impact of a new medication. We recruit 200 participants for our study and randomly divide them as follows:</p>
      <ul class="ul">
        <li>
          100 individuals to receive the medication—our treatment group
        </li>
        <li>
          100 individuals to receive a placebo—our control group
        </li>
      </ul>
      <p>In this case, we have two independent samples, each representing a distinct group, and we would utilize the unpaired form of the test statistic.</p>
      <h5 class="h6">
        Two-Sample Independent Z-Test (Population Variances Known)
      </h5>
      <p class="no-margin">
        When we have two independent samples and the population variances are <span class="bolded-texts">known</span>, we use the two-sample <span class="bolded-texts">independent z-test</span>
      </p>
      <p class="formulas-overflow">
        \[Z=\frac{(\bar{x}-\bar{y})-\mu_0}{\sqrt{\frac{\sigma_x^2}{n_x}+\frac{\sigma_y^2}{n_y}}}\]
      </p>
      <p>In this formula, we have:</p>
      <ul class="ul">
        <li><span class="inline-formulas">\[\bar{x}\]</span> – the sample mean of the first sample</li>
        <li><span class="inline-formulas">\[\bar{y}\]</span> – the sample mean of the second sample</li>
        <li><span class="inline-formulas">\[\mu_0\]</span> – the hypothesized mean difference (usually 0)</li>
        <li><span class="inline-formulas">\[\sigma_x^2\]</span> – the population variance of the first population</li>
        <li><span class="inline-formulas">\[\sigma_y^2\]</span> – the population variance of the second population</li>
        <li><span class="inline-formulas">\[n_x\]</span> – the sample size of the first sample</li>
        <li><span class="inline-formulas">\[n_y\]</span> – the sample size of the second sample</li>
      </ul>
      <p>The independent z-test relies on several key assumptions:</p>
      <ul class="ul">
        <li>The data is continuous (not discrete).</li>
        <li>The samples from the two groups must be independent of each other.</li>
        <li>The data in each group should follow a normal distribution. This is important because the z-test relies on the assumption of a normal distribution to calculate probabilities accurately.</li>
        <li>The population standard deviation of the two samples is known.</li>
        <li>Both samples ends must be higher than 30.</li>
      </ul>
      <p>Please note that when the population variances are known and the sample size of each group is lower than 30, it is common to use the t-test instead of the z-test for finding the difference in means. The t-test is appropriate in this scenario because the sample size is small and using the t-distribution provides more accurate results.</p>
      <p>When the samples are independent, and the population standard deviation is unknown, we assume that population standard deviations are equal or unequal.</p>
      <h5 class="h6">
        Two-Sample Independent T-Test (Population Variances Are Unknown but Assumed Equal)
      </h5>
      <p>One example of a t-test for difference in means where we assume that the population standard deviations are unknown is comparing the effectiveness of two different teaching methods in improving students' math skills.</p>
      <p>In this scenario, we randomly select 30 students; assign one half of the 30 to Method A and the other to Method B. After a certain period of time, we measure the math scores of both groups, assuming that the population variances of the two are unknown but equal. We want to determine if there is a significant difference in the mean math scores between Methods A and B.</p>
      <p class="no-margin">
        When the population standard deviations are <span class="bolded-texts">unknown</span> but assumed <span class="bolded-texts">equal</span>, we combine the observations from both samples to calculate a pooled estimate of the common population variance. This is then used in the calculation of the test statistic for the difference between the two means:
      </p>
      <p class="formulas-overflow">
        \[T=\frac{(\bar{x}-\bar{y})-\mu_0}{\sqrt{\frac{s_p^2}{n_x}+\frac{s_p^2}{n_y}}}\]
      </p>
      <p class="no-margin">
        We interpret the formula in the following way:
      </p>
      <p class="formulas-overflow">
        \[s_p^2=\frac{(n_x-1)s_x^2+(n_y-1)s_y^2}{n_x+n_y-2}\]
      </p>
      <p>In this formula, we have:</p>
      <ul class="ul">
        <li><span class="inline-formulas">\[\bar{x}\]</span> – the sample mean of first sample</li>
        <li><span class="inline-formulas">\[\bar{y}\]</span> – the sample mean of second sample</li>
        <li><span class="inline-formulas">\[\mu_0\]</span> – the hypothesized mean difference (usually 0)</li>
        <li><span class="inline-formulas">\[n_x\]</span> – the sample size of the first sample</li>
        <li><span class="inline-formulas">\[n_y\]</span> – the sample size of the second sample</li>
        <li><span class="inline-formulas">\[n_x+n_y-2\]</span> – the number of degrees of freedom</li>
      </ul>
      <p>These are the assumptions behind the two-sample independent t-test (assuming equal variances):</p>
      <ul class="ul">
        <li>The two populations are normally distributed.</li>
        <li>The unknown population variances are equal.</li>
        <li>The observations within each sample and the observations between the two samples are assumed to be independent of each other.</li>
      </ul>
      <h5 class="h6">
        Two-Sample Independent T-Test (Population Variances Are Unknown but Assumed Unequal)
      </h5>
      <p>When we have two populations assumed to be normally distributed, but we do not know the population variances and whether they’re equal, we can use Welch's t-test based on independent random samples. This test allows us to compare the means and calculate if there is a significant difference between the two populations.</p>
      <p>For example, suppose we want to compare the effectiveness of two different weight loss programs. Additionally, we randomly select two groups of individuals:</p>
      <ul class="ul">
        <li>Group A follows the first program, denoted Program A</li>
        <li>Group B follows the second program, denoted Program B</li>
      </ul>
      <p>We measure both groups’ results after a specified duration as we aim to determine if there is a significant difference in the mean weight loss between the two programs. In this case, we use a modified version of the independent samples t-test that accounts for unequal variances.</p>
      <p class="no-margin">
        The formula for Welch's t-test is as follows:
      </p>
      <p class="formulas-overflow">
        \[T=\frac{(\bar{x}-\bar{y})-\mu_0}{\sqrt{\frac{s_x^2}{n_x}+\frac{s_y^2}{n_y}}}\]
      </p>
      <p class="no-margin">
        We interpret the formula in the following way:
      </p>
      <p class="formulas-overflow">
        \[df=\frac{\left(\frac{s_x^2}{n_x}+\frac{s_y^2}{n_y}\right)^2}{\frac{\left(s_x^2/n_x\right)^2}{n_x-1}+\frac{\left(s_y^2/n_y\right)^2}{n_y-1}}\]
      </p>
      <ul class="ul">
        <li><span class="inline-formulas">\[\bar{x}\]</span> – the sample mean of first sample</li>
        <li><span class="inline-formulas">\[\bar{y}\]</span> – the sample mean of second sample</li>
        <li><span class="inline-formulas">\[\mu_0\]</span> – the hypothesized mean difference (usually 0)</li>
        <li><span class="inline-formulas">\[s_x^2\]</span> – the sample variance of the first population</li>
        <li><span class="inline-formulas">\[s_y^2\]</span> – the sample variance of the second population</li>
        <li><span class="inline-formulas">\[n_x\]</span> – the sample size of the first sample</li>
        <li><span class="inline-formulas">\[n_y\]</span> – the sample size of the second sample</li>
      </ul>
      <p>These are the assumptions behind the two-sample independent t-test (assuming unequal variances):</p>
      <ul class="ul top-margins">
        <li>The two populations are normally distributed.</li>
        <li>The unknown population variances are unequal.</li>
        <li>The observations within each sample and the observations between the two samples are assumed to be independent of each other.</li>
      </ul>
      <img class="last-image-desktop" src="../../../../assets/images/calculators/difference-in-means/dif-in-means-02.webp" alt="Whole calculation process graph.">
      <img class="last-image-mobile" src="../../../../assets/images/calculators/difference-in-means/dif-in-means-02-mobile.webp" alt="Whole calculation process graph.">
    </div>
  </div>
</template>

<script>
export default {
  mounted() {
    this.renderMathJax();
  },
  methods: {
    renderMathJax() {
      if (window.MathJax) {
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            processEscapes: true,
          },
          'HTML-CSS': {
            linebreaks: { automatic: true, width: 'container' },
          },
          SVG: {
            linebreaks: { width: '10000px' },
          },
          jax: ['input/TeX', 'output/SVG'],
        });
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.articles-container {
  .article-section {
    p {
      margin: 0 0 var(--spacing-20);

      &.formulas-overflow {
        margin: 0;

        &.scrolled {
          margin-bottom: var(--spacing-20);
        }
      }

      &:last-of-type {
        margin: 0;
      }

      .links {
        display: inline;
      }

      &.no-margin {
        margin: 0;
      }
    }

    .ul {
      padding-left: 27px;
      margin-bottom: var(--spacing-20);
    }

    .last-image-mobile {
      display: none;
    }

    .h5-heading {
      margin: var(--spacing-40) 0;
    }

    .colored-bolded-centered-texts {
      font-weight: 600;
      color: var(--accent-shade-3);
      text-align: center;
    }

    .ol-ul-top-bottom-margins.ol-ul-top-bottom-margins {
      margin: var(--spacing-20) 0 var(--spacing-40);
    }

    .margins-22.margins-22 {
      margin: 2px 0 var(--spacing-22);
    }

    .image-headings {
      font-weight: 600;
      font-size: 22px;
      margin: var(--spacing-80) 0 0;
      text-align: center;
      color: var(--accent-shade-3);

      & ~ * {
        margin-top: 0;
      }

      &.additional {
        margin: var(--spacing-20) 0 var(--spacing-24);
      }
    }
  }
}

@include bp($bp-laptop) {
  .articles-container {
    .article-section {
      .h5-heading {
        font-size: 18px;
      }
    }
  }
}

@include bp($bp-mobile) {
  .articles-container {
    .article-section {
      .last-image-desktop {
        display: none;
      }

      .last-image-mobile {
        display: inline;
      }

      .margin-bottom-32-mobile {
        margin-bottom: var(--spacing-32);
      }

      .no-bottom-margin-mobile {
        margin-bottom: 0;
      }

      .h5-heading {
        font-size: 18px;
        margin-bottom: var(--spacing-32);
      }

      .image-headings {
        font-size: 18px;
        margin-top: var(--spacing-32);

        &.top-margin-60 {
          margin-top: var(--spacing-60);
        }

        &.additional {
          margin: 0 0 var(--spacing-18);
        }
      }
    }
  }
}

@import '../../../../assets/sass/calculators/articles.scss';
</style>
